"use client"

import * as React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import type { FieldErrors } from "react-hook-form"

const contactFormSchema = z.object({
  nome: z.string().nonempty("Nome é obrigatório"),
  email: z.string().email("Email inválido"),
  assunto: z.string().nonempty("Assunto é obrigatório"),
  mensagem: z.string().nonempty("Mensagem é obrigatória"),
})

type ContactFormValues = z.infer<typeof contactFormSchema>

export function ContactForm() {
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      nome: "",
      email: "",
      assunto: "",
      mensagem: "",
    },
  })

  // Callback para erros de validação
  const onError = (errors: FieldErrors<ContactFormValues>) => {
    Object.values(errors).forEach((err) => {
      if (err?.message) {
        toast({
          variant: "destructive",
          title: String(err.message),
        })
      }
    })
  }

  const onSubmit = async (values: ContactFormValues) => {
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      })

      if (response.ok) {
        toast({
          title: "Email enviado!",
          description: "O seu formulário de contacto foi enviado com sucesso.",
        })
        form.reset()
      } else {
        const data = await response.json()
        toast({
          variant: "destructive",
          title: "Erro ao enviar",
          description: data.error || "Algo correu mal.",
        })
      }
    } catch {
      toast({
        variant: "destructive",
        title: "Erro ao enviar",
        description: "Não foi possível enviar o formulário. Tente novamente.",
      })
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, onError)} className="space-y-4" data-test="contact-form" id="contact">
        <FormField
          control={form.control}
          name="nome"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome</FormLabel>
              <FormControl>
                <Input data-test="nome-input" placeholder="Digite o seu nome" {...field} />
              </FormControl>
              <FormMessage data-test="nome-error" />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input data-test="email-input" type="email" placeholder="seu@email.com" {...field} />
              </FormControl>
              <FormMessage data-test="email-error" />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="assunto"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Assunto</FormLabel>
              <FormControl>
                <Input data-test="assunto-input" placeholder="Assunto da mensagem" {...field} />
              </FormControl>
              <FormMessage data-test="assunto-error" />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="mensagem"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mensagem</FormLabel>
              <FormControl>
                <Textarea data-test="mensagem-input" placeholder="Escreva a sua mensagem" {...field} />
              </FormControl>
              <FormMessage data-test="mensagem-error" />
            </FormItem>
          )}
        />
        <Button type="submit" data-test="enviar-mensagem-button" className="w-full bg-black text-white hover:bg-gray-800">
          Enviar Mensagem
        </Button>
      </form>
    </Form>
  )
} 